import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, isValidElement, cloneElement } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

export enum PreloadingMode {
	onlyJS = 'onlyJS',
	onlyJSAndCriticalData = 'onlyJSAndCriticalData',
	everything = 'everything',
}

export type PreloaderOptions = {
	preloadingMode?: PreloadingMode;
} & Record<string, string | boolean | number | undefined>;

type TransitionPreloaderProps = {
	name?: string;
	children: ReactNode;
	preloadUrl: string;
	preloadOptions?: PreloaderOptions;
};

const PRELOAD_TIMER = 100;

const sendAnalyticsEvent = (
	createAnalyticsEvent: ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'],
	action: string,
	name: string | undefined,
	attributes: Record<string, any>,
) => {
	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action,
			actionSubject: 'hoverPreloader',
			actionSubjectId: name ?? 'n/a',
			source: 'hoverPreloader',
			attributes,
		},
	}).fire();
};

const preload = (preloadUrl: string, options: PreloaderOptions = {}) => {
	window.dispatchEvent(
		new CustomEvent('preloadOnHover', {
			bubbles: true,
			detail: { preloadUrl, ...options },
		}),
	);
};

const TransitionPreloaderImpl = ({
	name,
	children,
	preloadUrl,
	preloadOptions,
}: TransitionPreloaderProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const hoverTimeoutRef = useRef<number | null>(null);
	const preloadRef = useRef<boolean>(false);

	useEffect(() => {
		return () => {
			preloadRef.current = false;
			if (hoverTimeoutRef.current) {
				window.clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	// Here we implement multi-stage preloading. JS can be preloaded immediately and unconditionally.
	// For the rest of the preload options / experiments require at least a timeout, but other cases
	// may require more sophisticated logic to decide when or whether to preload.
	const onMouseEnter = useCallback(() => {
		preloadRef.current = false;

		preload(preloadUrl, { preloadingMode: PreloadingMode.onlyJS });

		// Do not proceed to the next stage if only JS preloading is enabled
		if (preloadOptions?.preloadingMode === PreloadingMode.onlyJS) {
			return;
		}

		// Trigger next preloading stage either if everything is enabled or if critical data preloading
		// is enabled and the corresponding feature flag is enabled.
		const shouldPreload =
			preloadOptions?.preloadingMode === PreloadingMode.everything
				? true
				: preloadOptions?.preloadingMode === PreloadingMode.onlyJSAndCriticalData &&
					fg('confluence_preload_critical_data');

		if (!shouldPreload) {
			return;
		}

		if (hoverTimeoutRef.current) {
			window.clearTimeout(hoverTimeoutRef.current);
		}

		hoverTimeoutRef.current = window.setTimeout(() => {
			preload(preloadUrl, preloadOptions);
			preloadRef.current = true;
		}, PRELOAD_TIMER);
	}, [preloadUrl, preloadOptions]);

	const onMouseLeave = useCallback(() => {
		if (hoverTimeoutRef.current) {
			window.clearTimeout(hoverTimeoutRef.current);
		}

		if (preloadRef.current) {
			sendAnalyticsEvent(createAnalyticsEvent, 'preloadAndLeave', name, {
				preloadingMode: preloadOptions?.preloadingMode,
			});
		}
	}, [createAnalyticsEvent, name, preloadOptions?.preloadingMode]);

	const onMouseDown = useCallback(() => {
		preloadRef.current = false;
	}, []);

	// Helper to chain child handlers with hover-preloader handlers
	const chain =
		<T extends (...args: any[]) => void | undefined>(...fns: (T | undefined)[]) =>
		(...args: Parameters<NonNullable<T>>) => {
			fns.forEach((fn) => {
				if (fn) {
					fn(...args);
				}
			});
		};
	const wrappedChild = isValidElement(children)
		? cloneElement(children, {
				onMouseEnter: chain(children.props.onMouseEnter, onMouseEnter),
				onFocus: chain(children.props.onFocus, onMouseEnter),
				onMouseLeave: chain(children.props.onMouseLeave, onMouseLeave),
				onBlur: chain(children.props.onBlur, onMouseLeave),
				onMouseDown: chain(children.props.onMouseDown, onMouseDown),
			})
		: children;
	return (
		<div data-vc={`hover-preloader-${name}`} data-testid="hover-preloader">
			{wrappedChild}
		</div>
	);
};

export const HoverPreloader = (props: TransitionPreloaderProps) => {
	if (!props.preloadUrl) {
		if (expVal('platform_editor_hydratable_ui', 'isEnabled', false)) {
			return (
				<div data-vc={`hover-preloader-${props.name}`} data-testid="hover-preloader">
					{props.children}
				</div>
			);
		}

		return <>{props.children}</>;
	}
	return <TransitionPreloaderImpl {...props} />;
};

export const PreloadingPresets = {
	onlyJS: { preloadingMode: PreloadingMode.onlyJS } as const,
	onlyJSAndCriticalData: { preloadingMode: PreloadingMode.onlyJSAndCriticalData } as const,
	everything: { preloadingMode: PreloadingMode.everything } as const,
};
